import { queriesPayrollRuns } from "@query";
import {
  WSEmptyState,
  WSInfiniteScroll,
  WSList,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  ListFormsSortParameterValue,
  PayrollRunType
} from "@wingspanhq/wingspan-api-sdk";
import React from "react";

import { useBrowserPageTitle } from "../../../components/BrowserPageTitle/BrowserPageTitle";
import { TablePayrollsContractor } from "../../PayrollRuns/components/TablePayrollsContractor/TablePayrollsContractor";
import { routeHelpersPayablesPayrollRun } from "../paths";

/**
 * The Payables "Payroll Runs" tab: contractor payable execution, listed off v3
 * `listPayrollRuns` filtered to `ContractorVendor`.
 *
 * Laid out as the retired v1 `PayablesPayrollRuns` screen was — heading, one
 * line of copy, and the table, with no filters and no export. The employee
 * payroll surface under `/member/payroll-runs` is a different screen built to
 * the new design; nothing here is shared with it beyond the underlying query.
 *
 * A row opens `PATH_PAYABLES_PAYROLL_RUN` — since WIN-15815 that is the v3
 * payroll run details screen, so the id in that route is a payroll run id.
 */
export const RoutePayablesPayrollRuns: React.FC = () => {
  useBrowserPageTitle("Payroll Runs");

  const goToPayrollRun = routeHelpersPayablesPayrollRun.useGoTo();

  // Newest-first, as v1's "Date paid" column defaulted to. `sort` landed in
  // SDK 4.12.1 (PLA-3610); before that the order was whatever the endpoint
  // returned.
  const query = queriesPayrollRuns.listPayrollRuns.usePaginatedQuery({
    filter: { type: { eq: PayrollRunType.CONTRACTOR_VENDOR } },
    sort: { payDate: ListFormsSortParameterValue.DESC }
  });
  const payrollRuns = query.flatData ?? [];

  const renderContent = () => {
    if (query.isInitialLoading) {
      return <TablePayrollsContractor items={[]} loading />;
    }

    if (query.isError) {
      return (
        <WSEmptyState
          actions={{
            buttons: [{ label: "Try again", onClick: () => query.refetch() }]
          }}
          description="Something went wrong loading payroll runs."
          title="Couldn't load payroll runs"
          type="error"
        />
      );
    }

    if (payrollRuns.length === 0) {
      return (
        <WSEmptyState
          description="A record of every payroll run and its corresponding details will display here."
          title="No payroll runs"
          type="documents"
        />
      );
    }

    return (
      <WSInfiniteScroll
        endOfList={!query.hasNextPage}
        loading={!!query.isFetchingNextPage}
        loadMore={payrollRuns.length > 0}
        onLoad={() => query.fetchNextPage()}
      >
        <TablePayrollsContractor
          items={payrollRuns}
          onRowOpen={payrollRunId => goToPayrollRun({ payrollRunId })}
        />
      </WSInfiniteScroll>
    );
  };

  return (
    <WSList gap="XL">
      <WSList gap="M">
        <WSText.Heading1>Payroll Runs</WSText.Heading1>
        <WSText color="gray600">The full history of payments sent.</WSText>
      </WSList>
      {renderContent()}
    </WSList>
  );
};
